import {
  HealthNoteReleaseContent,
  HealthNoteReleaseDialog,
  HealthNoteReleaseLayout,
  HealthNoteReleaseTitle,
} from "@Components/game/health-and-safety/health-and-safety-notification.styled";
import { sanitizeHealthNoteContent } from "@Components/game/health-and-safety/sanitize-health-note";
import type { HealthNote } from "@Shared/schemas/notes/noteSchemas";
import Fade from "@mui/material/Fade";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useId, useMemo, useRef, useState } from "react";

export const HEALTH_NOTE_DISPLAY_MS = 4_000;
export const HEALTH_NOTE_FADE_OUT_MS = 600;

export interface IHealthAndSafetyNotification {
  note: HealthNote | null;
  onElapsed: (noteId: number) => void;
}

const TimedHealthNote = ({ note, onElapsed }: IHealthAndSafetyNotification & { note: HealthNote }) => {
  const [open, setOpen] = useState(true);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const titleId = useId();
  const contentId = useId();
  const content = useMemo(() => sanitizeHealthNoteContent(note.content), [note.content]);
  const onElapsedRef = useRef(onElapsed);
  onElapsedRef.current = onElapsed;
  const timer = useRef({
    timeout: null as ReturnType<typeof setTimeout> | null,
    startedAt: null as number | null,
    remaining: HEALTH_NOTE_DISPLAY_MS,
    entered: false,
    completed: false,
    notified: false,
  });

  const pause = () => {
    const current = timer.current;
    if (current.timeout !== null) clearTimeout(current.timeout);
    if (current.startedAt !== null) current.remaining = Math.max(0, current.remaining - (Date.now() - current.startedAt));
    current.timeout = null;
    current.startedAt = null;
  };

  const resume = () => {
    const current = timer.current;
    if (!current.entered || current.completed || current.timeout !== null || document.visibilityState === "hidden") return;
    current.startedAt = Date.now();
    current.timeout = setTimeout(() => {
      current.timeout = null;
      current.startedAt = null;
      current.completed = true;
      setOpen(false);
    }, current.remaining);
  };

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") pause();
      else resume();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    handleVisibility();
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      pause();
    };
  }, []);

  return (
    <HealthNoteReleaseDialog
      open={open}
      disableEscapeKeyDown
      TransitionComponent={Fade}
      transitionDuration={{ enter: 0, exit: reduceMotion ? 0 : HEALTH_NOTE_FADE_OUT_MS }}
      aria-labelledby={titleId}
      aria-describedby={contentId}
      PaperProps={{ lang: note.locale }}
      onKeyDown={event => event.stopPropagation()}
      TransitionProps={{
        onEntered: () => {
          timer.current.entered = true;
          resume();
        },
        onExited: () => {
          if (!timer.current.completed || timer.current.notified) return;
          timer.current.notified = true;
          onElapsedRef.current(note.id);
        },
      }}
    >
      <HealthNoteReleaseLayout>
        <HealthNoteReleaseTitle id={titleId}>{note.title}</HealthNoteReleaseTitle>
        <HealthNoteReleaseContent id={contentId} tabIndex={0} dangerouslySetInnerHTML={{ __html: content }} />
      </HealthNoteReleaseLayout>
    </HealthNoteReleaseDialog>
  );
};

export const HealthAndSafetyNotification = ({ note, onElapsed }: IHealthAndSafetyNotification) =>
  note ? <TimedHealthNote key={`${note.id}`} note={note} onElapsed={onElapsed} /> : null;
